import React, { useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StyleSheet, View, ScrollView, SafeAreaView, KeyboardAvoidingView } from 'react-native';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import Button from '../Button';
import ExpenseFormFields from './ExpenseFormFields';
import { Modals, Screens, Stacks } from '../../constants/ScreenNames';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExpense,
  initiateExpenseValues,
  SET_EXPENSE_RESULTS,
  updateExpense,
} from '../../store/actions/expense';
import {
  selectCurrentExpenseReportCardModalItem,
  selectCurrentReportIdForUpdate,
  selectExpenseStates,
  selectSelectedChargesQueue,
} from '../../store/selectors/expense.selector';
import ReceiptsBar from './ReceiptsBar';
import {
  EXPENSE_ACTION_TYPES,
  EXPENSE_FORM_KEYS,
  EXPENSE_SUB_TAB_OPTIONS,
  expenseActions,
} from '../../constants/Enums';
import { scale, scaleVertically } from '../../utils/scale.utils';
import useExpenseFormValidation from '../../utils/useExpenseFormValidation';
import { REPORT_TOGGLE_KEY, REPORT_TOGGLE_OPTIONS } from '../../constants/ExpenseCategories';
import LoaderOverlay from '../modals/LoaderOverlay';
import { performExpenseAction } from '../../services/api/expense.service';
import useExpenseHeaderSetup from '../../utils/useExpenseHeaderSetup';
import { isNullOrEmpty } from '../../utils/common.utils';

const getReportId = ({
  isCreationForm,
  expenseStates,
  selectedReport,
  currentExpenseReportCardModalItem,
  fromExistingReport,
  totalCharges = null,
  currentChargeIndex = null,
  firstExpenseReportId = null, // New parameter to store the report ID from the first expense
}) => {
  if (isCreationForm) {
    if (fromExistingReport) {
      return currentExpenseReportCardModalItem.ReportId;
    }

    if (!isNullOrEmpty(totalCharges)) {
      if (expenseStates[REPORT_TOGGLE_KEY] === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label) {
        if (currentChargeIndex === 0) {
          // For the first expense, return "0" to create a new report
          return REPORT_TOGGLE_OPTIONS.NEW_REPORT.value;
        } else {
          // For subsequent expenses, use the report ID from the first expense
          return firstExpenseReportId;
        }
      }
      return selectedReport.ReportId;
    }

    if (expenseStates[REPORT_TOGGLE_KEY] === REPORT_TOGGLE_OPTIONS.NEW_REPORT.label) {
      return REPORT_TOGGLE_OPTIONS.NEW_REPORT.value;
    }
    return selectedReport.ReportId;
  }
  return currentExpenseReportCardModalItem.ReportId;
};

const ExpenseFormStepTwo = () => {
  const dispatch = useDispatch();
  const route = useRoute();
  const navigation = useNavigation();
  const {
    isCreationForm,
    subTab,
    isEditable,
    selectedCharge = null,
    lockedFields = [],
    fromExistingReport = false,
    isMileage = false,
    totalCharges = null,
    currentChargeIndex = null,
    firstExpenseReportId = null,
  } = route.params || {};

  const actionType = isCreationForm ? EXPENSE_ACTION_TYPES.CREATION : EXPENSE_ACTION_TYPES.UPDATE;
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const selectedReport = useSelector(selectCurrentReportIdForUpdate);
  const selectedChargesQueue = useSelector(selectSelectedChargesQueue);
  const currentExpenseReportCardModalItem = useSelector(selectCurrentExpenseReportCardModalItem);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmitDisabled = !useExpenseFormValidation({
    step: EXPENSE_FORM_KEYS.STEP_TWO,
    actionType,
    subTab,
  });

  const reportId = getReportId({
    isCreationForm,
    expenseStates,
    selectedReport,
    currentExpenseReportCardModalItem,
    fromExistingReport,
    totalCharges,
    currentChargeIndex,
    firstExpenseReportId, // Pass the first expense's report ID
  });

  useExpenseHeaderSetup({
    navigation,
    reportNumber: currentExpenseReportCardModalItem?.ReportNumber,
    fromExistingReport,
    isCreationForm,
  });

  const getSubmitButtonLabel = () => {
    // If there are charges, and it's not the last charge, show "Create and Continue"
    if (totalCharges && currentChargeIndex < totalCharges - 1) {
      return 'Create & Continue';
    }

    // Else, show 'Create Expense' (for creation) or 'Save' (for update)
    return isCreationForm ? 'Create Expense' : 'Save';
  };

  const cleanReduxExpenseState = () => {
    // Cleans the redux state after creating the expense
    const formFields = {};
    dispatch(
      initiateExpenseValues({
        actionType,
        subTab,
        formFields,
      })
    );
  };

  const getReportsList = async () => {
    try {
      const response = await performExpenseAction({
        ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES,
      });
      dispatch({ type: SET_EXPENSE_RESULTS, list: response });
      return JSON.parse(response);
    } catch (err) {
      return null;
    }
  };

  const getResultingReport = async (resultingReportId) => {
    const reportsList = await getReportsList();
    return reportsList.find(({ ReportId }) => ReportId === resultingReportId);
  };

  const navigateToResultingReport = async (resultingReportId) => {
    const resultingReport = await getResultingReport(resultingReportId);
    if (isCreationForm) {
      // redux state needs to be cleaned only for creation flow
      cleanReduxExpenseState();
    }
    navigation.navigate(Stacks.ExpenseStack, {
      screen: Screens.Expense,
      params: { resultingReport },
    });
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (isCreationForm) {
        const response = await dispatch(
          await addExpense(expenseStates, reportId, selectedCharge, isMileage)
        );
        if (response) {
          cleanReduxExpenseState();

          if (!isNullOrEmpty(totalCharges) && currentChargeIndex < totalCharges - 1) {
            // Navigate back to CreateExpenseModal with next charge
            navigation.navigate(Modals.CreateExpenseModal, {
              selectedCharge: selectedChargesQueue[currentChargeIndex + 1],
              fromExistingReport: false,
              subTab: EXPENSE_SUB_TAB_OPTIONS.CHARGES,
              totalCharges,
              currentChargeIndex: currentChargeIndex + 1,
              firstExpenseReportId: response?.ReportId, // Pass the report ID
            });
          } else {
            // All charges processed
            navigation.navigate(Stacks.ExpenseStack, {
              screen: Screens.Expense,
              params: { resultingReport: await getResultingReport(response?.ReportId) },
            });
          }
        }
      } else {
        await updateExpense(reportId, expenseStates);
        navigateToResultingReport(reportId);
      }
    } catch (err) {
      setIsSubmitting(false);
      console.error({ err });
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      {isSubmitting && (
        <LoaderOverlay
          loadingText={isCreationForm ? 'Creating your expense...' : 'Saving your expense...'}
        />
      )}
      <KeyboardAvoidingView style={styles.container} behavior="padding">
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          automaticallyAdjustKeyboardInsets={true}
          keyboardShouldPersistTaps="handled"
        >
          {!isMileage && (
            <ReceiptsBar
              isEditable={isEditable}
              actionType={actionType}
              subTab={subTab}
              updateExpenseState={false}
            />
          )}
          <View style={CommonStyles.mx1}>
            <ExpenseFormFields
              isEditable={isEditable}
              step={EXPENSE_FORM_KEYS.STEP_TWO}
              lockedFields={lockedFields}
              isCreationForm={isCreationForm}
              subTab={subTab}
              fromExistingReport={fromExistingReport}
            />
          </View>

          <View style={styles.safeArea} />
          {isEditable && (
            <View style={styles.buttonContainer}>
              <Button
                label={getSubmitButtonLabel()}
                style={[
                  styles.submitButton,
                  isSubmitDisabled && {
                    backgroundColor: Color.lightgray,
                  },
                ]}
                labelStyle={[
                  CommonStyles.buttonFont,
                  isSubmitDisabled && {
                    color: Color.disabledText,
                  },
                ]}
                onPress={onSubmit}
                disabled={isSubmitDisabled}
              />
            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const base = scale(12 * 0.8);

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    ...CommonStyles.mx4,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  buttonContainer: {},
  submitButton: {
    ...CommonProperties.py2,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
    // ...CommonProperties.mx4,
    ...CommonProperties.mb4,
    bottom: scaleVertically(15),
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ExpenseFormStepTwo;
